import React, { useEffect, useRef, useState } from "react";
import { api, upload, type Me } from "./api";
import { AllergyBanner, type AllergyPanelData } from "./AllergyBanner";
import { AllergyResponseCard, type AllergyResponseOptions } from "./AllergyResponse";
import { Documents, eastern } from "./RecordScreen";
import { FLAG_LABEL, REASON_LABEL, HoldBadge } from "./ReviewQueue";
import { AddedSection, type AddedMed, type DocView } from "./StationAdded";
import { MedStatusBadge } from "./MedStatusBadge";
import { windowsForTimesOfDay, type WindowDef } from "../server/station/windows";

/**
 * The check-in med station (build plan §9.1 #3): look-up, then one screen per
 * person. Tablet-first (usable at 800 px), and nothing breaks at 375 px.
 * Buttons appear only for what this user may do; the server checks again.
 */

interface ParentRow {
  name: string;
  strength: string;
  doseQty: string;
  doseForm: string;
  timesOfDay: string[];
  timeOfDayOther: string;
}

interface MedView {
  medKey: string;
  receivedStatus: string;
  receivedExceptions: string[];
  receivedQuantity: string;
  receivedByEmail: string;
  receivedAtUtc: string;
  countVerifiedByEmail: string;
  controlled: boolean;
  selfCarry: string;
  confirmed: boolean;
  confirmedName: string;
  confirmedStrength: string;
  confirmedDose: string;
  confirmedRoute: string;
  confirmedWindows: string[];
  isPrn: string;
  prnInstructions: string;
  authorizationStatus: string;
  status: string;
  confirmedByEmail: string;
  confirmedAtUtc: string;
  supersedesMedKey: string;
  history: { medKey: string; receivedStatus: string; receivedAtUtc: string; receivedByEmail: string }[];
}

interface Visit {
  person: {
    personKey: string;
    lastName: string;
    firstName: string;
    dateOfBirth: string;
    ageAtProgram: string;
    turns18On: string;
    city: string;
    flags: { type: string }[];
    expectedReasons: string[];
    medStationStatus: string;
    medStationConfirmedByEmail: string;
    medStationConfirmedAtUtc: string;
    attendanceHold: string;
    adult: boolean;
    turns18Today: boolean;
    adult18NotifyConsent: string;
    adult18NotifyConsentAtUtc: string;
  };
  allergyBanner: AllergyPanelData;
  allergyResponse: AllergyResponseOptions;
  allergies: { confirmed: { rows: AllergyRow[]; medicationAllergy: string; confirmedByEmail: string; confirmedAtUtc: string } | null };
  application: {
    otc: { items: { id: string; name: string; permission: string }[] };
    topicals: { key: string; label: string; give: string; assist: string }[];
    selfCarry: { answer: string | null; types: string[] };
    documents: { fieldKey: string; label: string; uploaded: boolean; filename: string; contentType: string; isImage: boolean }[];
    medicationAllergyFollowUp: { answer: string | null; detail: string };
  } | null;
  seed: { frozen: boolean; frozenAtUtc: string; changedSinceFreeze: { label: string; added: string[]; removed: string[] }[] | null };
  medications: { index: number; parent: ParentRow; line: string; med: MedView | null }[];
  added: AddedMed[];
  otherMedications: MedView[];
  adultAgreements: DocView[];
  notes: { noteKey: string; noteType: string; body: string; authorName: string; authorEmail: string; createdUtc: string }[];
  options: { windows: { code: string; name: string; startTime: string; endTime: string }[]; exceptionCodes: string[]; routes: string[]; allergyCategories: string[]; authorizationUploaded: boolean; attestationText: string; selfAttestationText: string; addedSource: string };
  can: { receive: boolean; confirm: boolean; authorizeOnSite: boolean; attach: boolean; packet: boolean };
}

interface AllergyRow {
  allergy: string;
  reaction: string;
  category: string;
  anaphylaxisRisk: string;
  bringingEpiPen: string;
}

const STATUS_LABEL: Record<string, string> = { Outstanding: "Not started", InProgress: "In progress", Confirmed: "Done", NotRequired: "Not required" };
const RECEIPT_LABEL: Record<string, string> = { Received: "Received", ReceivedWithException: "Received with an exception", NotBrought: "Not brought", OnPerson: "On person (self-carry)" };
const EXCEPTION_LABEL: Record<string, string> = {
  NotOriginalContainer: "Not in the original container",
  LabelMismatch: "Label doesn't match",
  Quantity: "Quantity problem",
  Expired: "Expired",
  LoosePills: "Loose pills",
  Other: "Other",
};
const PERM: Record<string, string> = { yes: "Yes", no: "No", call: "Call first" };
const HELD = ["Received", "ReceivedWithException"];

function Badge({ s }: { s: string }) {
  const cls = s === "Confirmed" ? "bg-emerald-100 text-emerald-900" : s === "InProgress" ? "bg-sky-100 text-sky-900" : "bg-slate-100 text-slate-800";
  return <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${cls}`}>{STATUS_LABEL[s] ?? s}</span>;
}

// ---- look-up -----------------------------------------------------------------------------------

/** Live look-up: results from the first character, narrowing as you type (debounced). */
const SEARCH_DEBOUNCE_MS = 250;

function Search() {
  const [q, setQ] = useState("");
  const [people, setPeople] = useState<{ personKey: string; lastName: string; firstName: string; dateOfBirth: string; city: string; medStationStatus: string; expectedReasons: string[] }[] | null>(null);
  const [more, setMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);
  useEffect(() => {
    const term = q.trim();
    if (!term) {
      setPeople(null);
      setMore(false);
      return;
    }
    const mine = ++seq.current;
    const t = setTimeout(async () => {
      const r = await api<{ people: NonNullable<typeof people>; more: boolean }>("GET", `/api/station/search?q=${encodeURIComponent(term)}`);
      if (mine !== seq.current) return; // a later keystroke's answer wins
      if (r.ok) {
        setError(null);
        setPeople(r.data.people);
        setMore(r.data.more);
      } else setError(r.data.error ?? "Search failed.");
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q]);
  function onKey(e: React.KeyboardEvent) {
    if (e.key === "Enter" && people?.length === 1 && !more) window.location.assign(`/station/${encodeURIComponent(people[0].personKey)}`);
  }
  return (
    <section>
      <h2 className="mb-1 text-xl font-semibold">Med station</h2>
      <p className="mb-4 text-sm text-slate-600">
        Start typing a name, date of birth (e.g. 6/23/2009) or Reference Number. <a href="/pending" className="text-program underline">Pending authorizations</a> · <a href="/status-board" className="text-program underline">Status board</a>
      </p>
      <div className="section-card">
        <label htmlFor="st-q" className="field-label">Find</label>
        <input id="st-q" className="field-input w-full" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} autoFocus autoComplete="off" />
      </div>
      {error && <p className="field-error" role="alert">{error}</p>}
      {people && (
        <div className="section-card" aria-live="polite">
          {people.length === 0 ? (
            <p className="text-sm text-slate-600">No one matches.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {people.map((p) => (
                <li key={p.personKey} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                  <span className="min-w-0">
                    <a className="font-medium text-program underline" href={`/station/${encodeURIComponent(p.personKey)}`}>{p.lastName}, {p.firstName}</a>
                    <span className="block text-slate-600">{p.personKey} · born {p.dateOfBirth} · {p.city || "Not assigned"}</span>
                  </span>
                  <span className="flex items-center gap-2">
                    <a className="text-sm text-program underline" href={`/pass/student/${encodeURIComponent(p.personKey)}`}>Give now</a>
                    <Badge s={p.medStationStatus} />
                  </span>
                </li>
              ))}
            </ul>
          )}
          {more && <p className="mt-2 text-sm text-slate-600">Showing the first 25. Keep typing to narrow.</p>}
        </div>
      )}
    </section>
  );
}

// ---- the visit -----------------------------------------------------------------------------------

function usePost(personKey: string, onDone: (v: Visit) => void) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function run(path: string, body: unknown): Promise<boolean> {
    setBusy(true);
    setError(null);
    const r = await api<{ visit: Visit }>("POST", `/api/station/${encodeURIComponent(personKey)}/${path}`, body);
    setBusy(false);
    if (!r.ok) {
      setError(r.data.error ?? "That didn't save.");
      return false;
    }
    onDone(r.data.visit);
    return true;
  }
  return { busy, error, run };
}

/** 18+ (plan §9.1 #3): the notification question, required before the visit completes; the Adult Participant Agreement. */
function AdultConsent({ v, onDone }: { v: Visit; onDone: (v: Visit) => void }) {
  const { busy, error, run } = usePost(v.person.personKey, onDone);
  const p = v.person;
  const [agreement, setAgreement] = useState<File | null>(null);
  const [upErr, setUpErr] = useState<string | null>(null);
  const asked = p.adult18NotifyConsent !== "NotAsked";
  async function attach() {
    if (!agreement) return;
    setUpErr(null);
    const r = await upload<{ visit: Visit }>(`/api/station/${encodeURIComponent(p.personKey)}/adult-agreement?name=${encodeURIComponent(agreement.name)}`, agreement);
    if (r.ok) {
      setAgreement(null);
      onDone(r.data.visit);
    } else setUpErr(r.data.error ?? "The PDF didn't upload.");
  }
  return (
    <section className={`section-card ${asked ? "" : "border-amber-400"}`}>
      <h3 className="mb-1 text-base font-semibold">18 or older{p.turns18Today && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-950">Turns 18 today: ask consent today</span>}</h3>
      <p className="text-sm">Ask: <span className="font-medium">“Do you want your parent/guardian notified about medical visits?”</span></p>
      {asked ? (
        <p className="mt-1 text-sm text-emerald-800">Answer: <span className="font-semibold">{p.adult18NotifyConsent}</span> · {eastern(p.adult18NotifyConsentAtUtc)}</p>
      ) : (
        <p className="mt-1 text-sm text-amber-900">Not asked yet. Required before the visit is complete.</p>
      )}
      {v.can.receive && (
        <div className="mt-2 flex flex-wrap gap-2">
          <button type="button" className="btn-secondary" disabled={busy} onClick={() => run("adult-consent", { consent: "Yes" })}>Yes, notify my parent/guardian</button>
          <button type="button" className="btn-secondary" disabled={busy} onClick={() => run("adult-consent", { consent: "No" })}>No, don't notify</button>
        </div>
      )}
      <p className="mt-1 text-xs text-slate-500">You are recorded as the witness.</p>
      {error && <p className="field-error" role="alert">{error}</p>}
      <p className="mt-3 text-sm font-medium">Adult Participant Agreement</p>
      {v.adultAgreements.length ? (
        <ul className="text-sm">{v.adultAgreements.map((a) => <li key={a.docKey}><a className="text-program underline" href={`/api/med-documents/${encodeURIComponent(a.docKey)}`} target="_blank" rel="noreferrer">Signed agreement</a> · {eastern(a.uploadedAtUtc)} · {a.uploadedByEmail}</li>)}</ul>
      ) : (
        <p className="text-sm text-slate-600">None attached.</p>
      )}
      {v.can.confirm && (
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <input type="file" accept="application/pdf" className="text-sm" onChange={(e) => setAgreement(e.target.files?.[0] ?? null)} />
          <button type="button" className="btn-secondary" disabled={!agreement} onClick={attach}>Attach the signed agreement (PDF)</button>
        </div>
      )}
      {upErr && <p className="field-error" role="alert">{upErr}</p>}
    </section>
  );
}

function PreReview({ v, onDone }: { v: Visit; onDone: (v: Visit) => void }) {
  const { busy, error, run } = usePost(v.person.personKey, onDone);
  const [notes, setNotes] = useState("");
  const done = v.notes.filter((n) => n.noteType === "PreReviewDiscussed");
  return (
    <section className="section-card">
      <h3 className="mb-1 text-base font-semibold">Pre-review discussed with the parent</h3>
      {done.map((n) => (
        <p key={n.noteKey} className="text-sm"><span className="text-slate-500">{eastern(n.createdUtc)} · {n.authorName || n.authorEmail}:</span> {n.body}</p>
      ))}
      <label htmlFor="pr-notes" className="field-label mt-2">Notes (optional)</label>
      <textarea id="pr-notes" className="field-input min-h-16" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={4000} />
      {error && <p className="field-error" role="alert">{error}</p>}
      <button type="button" className="btn-secondary mt-2" disabled={busy} onClick={async () => (await run("pre-review", { notes })) && setNotes("")}>Discussed with the parent</button>
    </section>
  );
}

function Allergies({ v, onDone }: { v: Visit; onDone: (v: Visit) => void }) {
  const { busy, error, run } = usePost(v.person.personKey, onDone);
  const start = (): AllergyRow[] =>
    v.allergies.confirmed?.rows ??
    v.allergyBanner.allergies.map((a) => ({ allergy: a.allergy, reaction: a.reaction, category: a.category, anaphylaxisRisk: a.anaphylaxisRisk ?? "", bringingEpiPen: a.bringingEpiPen ?? "" }));
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState<AllergyRow[]>(start);
  const [medAllergy, setMedAllergy] = useState(v.allergies.confirmed?.medicationAllergy ?? v.allergyBanner.medicationAllergy ?? "");
  const set = (i: number, patch: Partial<AllergyRow>) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const c = v.allergies.confirmed;
  return (
    <section className="section-card">
      <h3 className="mb-1 text-base font-semibold">Allergies: confirm with the family</h3>
      {c ? (
        <div className="text-sm">
          <p className="text-emerald-800">Confirmed {eastern(c.confirmedAtUtc)} by {c.confirmedByEmail}.</p>
          <ul className="mt-1 list-disc pl-5">
            {c.rows.length === 0 && <li>No allergies.</li>}
            {c.rows.map((r, i) => (
              <li key={i}><span className="font-medium">{r.allergy}</span>{r.category ? ` (${r.category})` : ""}{r.reaction ? `: ${r.reaction}` : ""}{r.anaphylaxisRisk === "Yes" ? " — anaphylaxis risk" : ""}{r.bringingEpiPen === "Yes" ? " — has epinephrine" : ""}</li>
            ))}
          </ul>
          {c.medicationAllergy && <p className="mt-1">Medication allergy: {c.medicationAllergy}</p>}
        </div>
      ) : (
        <p className="text-sm text-amber-900">Not yet confirmed. The family's own list is in the banner above and is never changed.</p>
      )}
      {v.can.confirm && !editing && (
        <button type="button" className="btn-secondary mt-2" onClick={() => { setRows(start()); setEditing(true); }}>{c ? "Correct the confirmed list" : "Confirm or correct"}</button>
      )}
      {editing && (
        <div className="mt-3">
          {rows.map((r, i) => (
            <div key={i} className="mb-3 grid gap-2 border-b border-slate-100 pb-3 sm:grid-cols-2 lg:grid-cols-5">
              <div>
                <label htmlFor={`al-a-${i}`} className="field-label">Allergy</label>
                <input id={`al-a-${i}`} className="field-input" value={r.allergy} onChange={(e) => set(i, { allergy: e.target.value })} />
              </div>
              <div>
                <label htmlFor={`al-r-${i}`} className="field-label">Reaction</label>
                <input id={`al-r-${i}`} className="field-input" value={r.reaction} onChange={(e) => set(i, { reaction: e.target.value })} />
              </div>
              <div>
                <label htmlFor={`al-c-${i}`} className="field-label">Category</label>
                <select id={`al-c-${i}`} className="field-input" value={r.category} onChange={(e) => set(i, { category: e.target.value })}>
                  <option value="">—</option>
                  {v.options.allergyCategories.map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor={`al-x-${i}`} className="field-label">Anaphylaxis risk</label>
                <select id={`al-x-${i}`} className="field-input" value={r.anaphylaxisRisk} onChange={(e) => set(i, { anaphylaxisRisk: e.target.value })}>
                  <option value="">—</option><option value="Yes">Yes</option><option value="No">No</option>
                </select>
              </div>
              <div>
                <label htmlFor={`al-e-${i}`} className="field-label">Has epinephrine</label>
                <select id={`al-e-${i}`} className="field-input" value={r.bringingEpiPen} onChange={(e) => set(i, { bringingEpiPen: e.target.value })}>
                  <option value="">—</option><option value="Yes">Yes</option><option value="No">No</option>
                </select>
                <button type="button" className="mt-1 text-xs text-program underline" onClick={() => setRows(rows.filter((_x, j) => j !== i))}>Remove</button>
              </div>
            </div>
          ))}
          <button type="button" className="btn-secondary" onClick={() => setRows([...rows, { allergy: "", reaction: "", category: "", anaphylaxisRisk: "", bringingEpiPen: "" }])}>Add an allergy</button>
          <label htmlFor="al-med" className="field-label mt-3">Allergic to any medication (blank if none)</label>
          <input id="al-med" className="field-input" value={medAllergy} onChange={(e) => setMedAllergy(e.target.value)} />
          {error && <p className="field-error" role="alert">{error}</p>}
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" className="btn-primary" disabled={busy} onClick={async () => (await run("allergies", { rows, medicationAllergy: medAllergy })) && setEditing(false)}>
              Confirm {rows.length === 0 ? "no allergies" : `${rows.length} allerg${rows.length === 1 ? "y" : "ies"}`}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      )}
    </section>
  );
}

function ReceiptSummary({ m }: { m: MedView }) {
  return (
    <p className="text-sm text-slate-700">
      <span className="font-medium">{RECEIPT_LABEL[m.receivedStatus] ?? m.receivedStatus}</span>
      {m.receivedExceptions.length > 0 && <> ({m.receivedExceptions.map((x) => EXCEPTION_LABEL[x] ?? x).join(", ")})</>}
      {m.receivedQuantity && <> · {m.receivedQuantity} counted</>}
      {m.controlled && <> · controlled{m.countVerifiedByEmail ? `, count verified by ${m.countVerifiedByEmail}` : ""}</>}
      <span className="block text-xs text-slate-500">by {m.receivedByEmail} · {eastern(m.receivedAtUtc)}</span>
    </p>
  );
}

function MedCard({ v, item, onDone }: { v: Visit; item: Visit["medications"][number]; onDone: (v: Visit) => void }) {
  const { busy, error, run } = usePost(v.person.personKey, onDone);
  const p = item.parent;
  const m = item.med;
  const [status, setStatus] = useState("Received");
  const [exceptions, setExceptions] = useState<string[]>([]);
  const [quantity, setQuantity] = useState("");
  const [controlled, setControlled] = useState(false);
  const [vEmail, setVEmail] = useState("");
  const [vPin, setVPin] = useState("");
  const [name, setName] = useState(p.name);
  const [strength, setStrength] = useState(p.strength);
  const [dose, setDose] = useState([p.doseQty, p.doseForm].filter(Boolean).join(" "));
  const [route, setRoute] = useState("");
  const [isPrn, setIsPrn] = useState((p.timesOfDay ?? []).length > 0 && p.timesOfDay.every((t) => t === "prn"));
  // Pre-selected from the parent's times of day where they named one (M3 Stage 3 fix 5); as needed: none.
  const [windows, setWindows] = useState<string[]>(() => windowsForTimesOfDay(p.timesOfDay, v.options.windows as WindowDef[]));
  const [prn, setPrn] = useState("");
  const [selfCarry, setSelfCarry] = useState(false);
  const [matches, setMatches] = useState(false);
  const held = HELD.includes(status);
  const receiptBody = { receivedStatus: status, exceptions: status === "ReceivedWithException" ? exceptions : [], quantity: held ? quantity : "", controlled, ...(controlled && held ? { verifier: { email: vEmail, pin: vPin } } : {}) };
  const confirmBody = { name, strength, dose, route, isPrn, windows, prnInstructions: isPrn ? prn : "", selfCarry, authorizationMatches: matches };
  // Not brought at check-in stays on the list, not yet received: "Receive now" later in the week.
  const notYet = m?.receivedStatus === "NotBrought";
  const [receiveNow, setReceiveNow] = useState(false);
  const needsReceipt = !m || (notYet && receiveNow);
  const canConfirm = v.can.confirm && (!m || (!m.confirmed && (!notYet || receiveNow)));
  const toggle = (list: string[], set: (x: string[]) => void, x: string) => set(list.includes(x) ? list.filter((y) => y !== x) : [...list, x]);
  const id = (k: string) => `m${item.index}-${k}`;

  return (
    <li className="rounded-md border border-slate-200 p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="min-w-0 break-words font-medium">{item.line}</p>
        <MedStatusBadge m={m} />
      </div>
      <p className="text-xs text-slate-500">The parent's entry (kept as written)</p>
      {m && <div className="mt-2"><ReceiptSummary m={m} /></div>}
      {m && m.history.length > 0 && (
        <p className="text-xs text-slate-500">Earlier: {m.history.map((h) => `${RECEIPT_LABEL[h.receivedStatus] ?? h.receivedStatus} (${eastern(h.receivedAtUtc)}, ${h.receivedByEmail})`).join("; ")}</p>
      )}
      {notYet && !receiveNow && v.can.receive && (
        <button type="button" className="btn-secondary mt-2" onClick={() => { setStatus("Received"); setReceiveNow(true); }}>Receive now</button>
      )}
      {m?.confirmed && (
        <p className="mt-1 text-sm">
          {m.confirmedName} {m.confirmedStrength} · {m.confirmedDose} · {m.confirmedRoute} · {m.isPrn === "Y" ? `As needed: ${m.prnInstructions}` : `Windows: ${m.confirmedWindows.join(", ")}`}
          {m.selfCarry === "Y" && " · self-carry"}
          <span className="block text-xs text-slate-500">confirmed by {m.confirmedByEmail} · {eastern(m.confirmedAtUtc)}</span>
        </p>
      )}
      {m?.confirmed && m.authorizationStatus !== "OnFile" && (
        <p className="mt-1 text-sm"><a className="text-program underline" href={`/authorizations/${encodeURIComponent(m.medKey)}`}>{m.authorizationStatus === "Pending" ? "Authorization (Path A on site · Path B remote)" : "Authorization details"}</a></p>
      )}

      {needsReceipt && (v.can.receive || v.can.confirm) && (
        <fieldset className="mt-3">
          <legend className="text-sm font-semibold">Receipt</legend>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm">
            {Object.entries(RECEIPT_LABEL).filter(([k]) => !(notYet && k === "NotBrought")).map(([k, l]) => (
              <label key={k} className="flex items-center gap-1.5"><input type="radio" name={id("st")} checked={status === k} onChange={() => setStatus(k)} />{l}</label>
            ))}
          </div>
          {status === "ReceivedWithException" && (
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
              {v.options.exceptionCodes.map((x) => (
                <label key={x} className="flex items-center gap-1.5"><input type="checkbox" checked={exceptions.includes(x)} onChange={() => toggle(exceptions, setExceptions, x)} />{EXCEPTION_LABEL[x] ?? x}</label>
              ))}
            </div>
          )}
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            {held && (
              <div>
                <label htmlFor={id("q")} className="field-label">Quantity counted</label>
                <input id={id("q")} className="field-input" inputMode="numeric" value={quantity} onChange={(e) => setQuantity(e.target.value.replace(/\D/g, ""))} />
              </div>
            )}
            <label className="flex items-center gap-2 self-end pb-2 text-sm"><input type="checkbox" checked={controlled} onChange={(e) => setControlled(e.target.checked)} />Controlled medication</label>
          </div>
          {controlled && held && (
            <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-3">
              <p className="text-sm font-medium text-amber-950">Second person: count it too, then sign with your own email and PIN on this device.</p>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                <div>
                  <label htmlFor={id("ve")} className="field-label">Second person's email</label>
                  <input id={id("ve")} className="field-input" type="email" autoComplete="off" value={vEmail} onChange={(e) => setVEmail(e.target.value)} />
                </div>
                <div>
                  <label htmlFor={id("vp")} className="field-label">Their PIN</label>
                  <input id={id("vp")} className="field-input" type="password" inputMode="numeric" autoComplete="off" maxLength={4} value={vPin} onChange={(e) => setVPin(e.target.value.replace(/\D/g, ""))} />
                </div>
              </div>
            </div>
          )}
        </fieldset>
      )}

      {canConfirm && status !== "NotBrought" && (
        <fieldset className="mt-3">
          <legend className="text-sm font-semibold">Rx review and confirm</legend>
          <div className="mt-1 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <div><label htmlFor={id("n")} className="field-label">Medication</label><input id={id("n")} className="field-input" value={name} onChange={(e) => setName(e.target.value)} /></div>
            <div><label htmlFor={id("s")} className="field-label">Strength</label><input id={id("s")} className="field-input" value={strength} onChange={(e) => setStrength(e.target.value)} /></div>
            <div><label htmlFor={id("d")} className="field-label">Dose</label><input id={id("d")} className="field-input" value={dose} onChange={(e) => setDose(e.target.value)} /></div>
            <div>
              <label htmlFor={id("r")} className="field-label">Route</label>
              <input id={id("r")} className="field-input" list={id("routes")} value={route} onChange={(e) => setRoute(e.target.value)} />
              <datalist id={id("routes")}>{v.options.routes.map((r) => <option key={r} value={r} />)}</datalist>
            </div>
          </div>
          {v.options.authorizationUploaded ? (
            <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-2">
              <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-0.5" checked={matches} onChange={(e) => setMatches(e.target.checked)} />I compared this to the Physician Medication Authorization on file — it matches</label>
              {!matches && <p className="mt-1 text-xs text-amber-900">Unchecked = held until a provider authorizes it (Path A or B).</p>}
            </div>
          ) : (
            <p className="mt-3 text-sm font-medium text-red-900">No medication authorization was uploaded: confirming records it as held, not given, until a provider authorizes it (Path A or B).</p>
          )}
          <label className="mt-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={isPrn} onChange={(e) => setIsPrn(e.target.checked)} />As needed (PRN)</label>
          {isPrn ? (
            <div className="mt-1"><label htmlFor={id("prn")} className="field-label">When to give it</label><input id={id("prn")} className="field-input" value={prn} onChange={(e) => setPrn(e.target.value)} /></div>
          ) : null}
          <p className="field-label mt-2">Windows{isPrn ? " (optional)" : ""}</p>
          {v.options.windows.length === 0 ? (
            <p className="text-sm text-amber-900">No medication windows are set (Config: Medication windows).</p>
          ) : (
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
              {v.options.windows.map((w) => (
                <label key={w.code} className="flex items-center gap-1.5"><input type="checkbox" checked={windows.includes(w.code)} onChange={() => toggle(windows, setWindows, w.code)} />{w.name} ({w.startTime}–{w.endTime})</label>
              ))}
            </div>
          )}
          {!(m?.selfCarry) && status !== "OnPerson" && <label className="mt-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={selfCarry} onChange={(e) => setSelfCarry(e.target.checked)} />Student self-carries this</label>}
        </fieldset>
      )}

      {error && <p className="field-error" role="alert">{error}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        {needsReceipt && v.can.receive && (
          <button type="button" className={canConfirm && status !== "NotBrought" ? "btn-secondary" : "btn-primary"} disabled={busy} onClick={() => run("medications/receive", { index: item.index, ...receiptBody })}>
            Record receipt only
          </button>
        )}
        {canConfirm && status !== "NotBrought" && (
          <button type="button" className="btn-primary" disabled={busy} onClick={() => run("medications/confirm", { index: item.index, ...(needsReceipt ? receiptBody : {}), ...confirmBody })}>
            {needsReceipt ? "Receive and confirm" : "Confirm"}
          </button>
        )}
      </div>
    </li>
  );
}

function VisitScreen({ personKey }: { personKey: string }) {
  const [v, setV] = useState<Visit | null>(null);
  const [error, setError] = useState<string | null>(null);
  const done = usePost(personKey, setV);
  useEffect(() => {
    void api<{ visit: Visit }>("GET", `/api/station/${encodeURIComponent(personKey)}`).then((r) => (r.ok ? setV(r.data.visit) : setError(r.data.error ?? "Could not load.")));
  }, [personKey]);
  if (error) return <div className="section-card" role="alert"><p className="text-sm text-red-700">{error}</p></div>;
  if (!v) return <p className="text-sm text-slate-600">Loading…</p>;
  const p = v.person;
  const flags = p.flags.filter((f) => f.type !== "ExpectedAtMedStation" && f.type !== "FamilyUpdatedAfterReview");
  return (
    <div>
      <p className="mb-3 text-sm"><a href="/station" className="text-program underline">← Med station</a></p>
      <AllergyBanner data={v.allergyBanner} />
      <section className="section-card">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-xl font-semibold">{p.lastName}, {p.firstName}</h2>
          <span className="flex items-center gap-2 text-sm text-slate-600">{p.personKey} <Badge s={p.medStationStatus} /> <a className="btn-secondary" href={`/pass/student/${encodeURIComponent(p.personKey)}`}>Give now</a></span>
        </div>
        <p className="mt-1 text-sm text-slate-700">
          Born {p.dateOfBirth} · age {p.ageAtProgram || "—"} · City: <span className="font-medium">{p.city || "Not assigned"}</span>
          {p.turns18On && <> · turns 18 on {p.turns18On}</>}
        </p>
        <div className="mt-2 flex flex-wrap gap-2 text-xs">
          <HoldBadge hold={p.attendanceHold} />
          {flags.map((f) => <span key={f.type} className="rounded bg-red-50 px-2 py-0.5 text-red-900">{FLAG_LABEL[f.type] ?? f.type}</span>)}
          {p.expectedReasons.length > 0 && <span className="rounded bg-sky-50 px-2 py-0.5 text-sky-900">Med station: {p.expectedReasons.map((r) => REASON_LABEL[r] ?? r).join(", ")}</span>}
        </div>
        {v.seed.frozen ? (
          v.seed.changedSinceFreeze && v.seed.changedSinceFreeze.length > 0 ? (
            <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-950">
              <p className="font-semibold">Changed since the seed was frozen ({eastern(v.seed.frozenAtUtc)}). The list below is the frozen seed.</p>
              {v.seed.changedSinceFreeze.map((c) => (
                <p key={c.label}>{c.label}: {[...c.added.map((l) => `now: ${l}`), ...c.removed.map((l) => `was: ${l}`)].join("; ")}</p>
              ))}
            </div>
          ) : (
            <p className="mt-2 text-xs text-slate-500">Medication seed frozen {eastern(v.seed.frozenAtUtc)}; no changes since.</p>
          )
        ) : (
          <p className="mt-2 text-xs text-amber-800">The medication seed is not frozen yet: the list below is the live application.</p>
        )}
      </section>

      {p.adult && <AdultConsent v={v} onDone={setV} />}
      <PreReview v={v} onDone={setV} />
      <Allergies v={v} onDone={setV} />
      <AllergyResponseCard personKey={p.personKey} panel={v.allergyBanner} options={v.allergyResponse} onPanel={(a) => setV({ ...v, allergyBanner: a })} />

      <section className="section-card">
        <h3 className="mb-2 text-base font-semibold">Medications ({v.medications.length})</h3>
        {v.medications.length === 0 ? <p className="text-sm text-slate-600">No medications listed.</p> : (
          <ul className="space-y-3">{v.medications.map((m) => <MedCard key={`${m.index}-${m.med?.medKey ?? ""}-${m.med?.confirmed ?? ""}`} v={v} item={m} onDone={setV} />)}</ul>
        )}
        {v.otherMedications.length > 0 && <p className="mt-2 text-xs text-slate-500">{v.otherMedications.length} other medication row(s) on file that match no line of the application.</p>}
        {v.application && (
          <p className="mt-3 text-sm">Self-carries emergency medication: <span className="font-medium">{v.application.selfCarry.answer ?? "not answered"}</span>{v.application.selfCarry.types.length ? ` — ${v.application.selfCarry.types.join(", ")}` : ""}</p>
        )}
      </section>

      <AddedSection
        added={v.added}
        onDone={setV}
        ctx={{ personKey: p.personKey, adult: p.adult, windows: v.options.windows, routes: v.options.routes, exceptionCodes: v.options.exceptionCodes, attestationText: v.options.attestationText, selfAttestationText: v.options.selfAttestationText, addedSource: v.options.addedSource, canReceive: v.can.receive }}
      />

      {v.application && (
        <section className="section-card">
          <h3 className="mb-2 text-base font-semibold">OTC and topical permissions</h3>
          <ul className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
            {v.application.otc.items.map((o) => <li key={o.id} className="flex justify-between gap-3 border-b border-slate-100"><span className="min-w-0">{o.name}</span><span>{PERM[o.permission] ?? o.permission}</span></li>)}
            {v.application.topicals.map((t) => <li key={t.key} className="flex justify-between gap-3 border-b border-slate-100"><span className="min-w-0">{t.label}</span><span>give {PERM[t.give] ?? t.give}, assist {PERM[t.assist] ?? t.assist}</span></li>)}
          </ul>
        </section>
      )}
      {v.application && <Documents personKey={p.personKey} docs={v.application.documents} />}

      <section className="section-card">
        <h3 className="mb-2 text-base font-semibold">Comments</h3>
        {v.notes.filter((n) => n.noteType === "Comment").length === 0 ? <p className="text-sm text-slate-600">None.</p> : (
          <ul className="space-y-2 text-sm">
            {v.notes.filter((n) => n.noteType === "Comment").map((n) => <li key={n.noteKey}><span className="text-xs text-slate-500">{n.authorName || n.authorEmail} · {eastern(n.createdUtc)}</span><p className="whitespace-pre-wrap break-words">{n.body}</p></li>)}
          </ul>
        )}
      </section>

      {v.can.confirm && p.medStationStatus !== "Confirmed" && (
        <section className="section-card">
          {done.error && <p className="field-error mb-2" role="alert">{done.error}</p>}
          <button type="button" className="btn-primary" disabled={done.busy} onClick={() => done.run("complete", {})}>Med station visit complete</button>
        </section>
      )}
      {p.medStationStatus === "Confirmed" && <p className="section-card text-sm text-emerald-800">Visit complete: {p.medStationConfirmedByEmail}, {eastern(p.medStationConfirmedAtUtc)}.</p>}
    </div>
  );
}

export function Station({ path }: { path: string; me: Me }): React.ReactElement {
  const key = path.startsWith("/station/") ? decodeURIComponent(path.slice("/station/".length)) : null;
  return key ? <VisitScreen key={key} personKey={key} /> : <Search />;
}
